/**
 * CommentModal — modal for adding a new comment.
 *
 * Guest mode: two fields — Name (optional, defaults to "Guest") and
 * Comment (basic rich-text editor with bold/italic/list).
 * Protected by Cloudflare Turnstile when configured.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Send, Loader2, Bold, Italic, List } from 'lucide-react';
import { TURNSTILE_CONFIG } from '../../config/integrations';
import { useAppSettings } from '../../context/AppSettingsContext';
import { postComment } from '../../services/api';
import { reportClientError } from '../../utils/errorReporting';
import './CommentModal.css';

interface CommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  estimateGuid: string;
  parentId?: number | null;
  parentAuthor?: string;
  onSuccess: () => void;
}

const GUEST_NAME_KEY = 'trusscore.comment.guestName';
/** Server-side limit; kept here so the message and the check can't disagree. */
const MAX_COMMENT_LENGTH = 5000;

export const CommentModal: React.FC<CommentModalProps> = ({
  isOpen,
  onClose,
  estimateGuid,
  parentId = null,
  parentAuthor,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation('results');
  const { settings: appSettings } = useAppSettings();
  const turnstileActive = TURNSTILE_CONFIG.enabled && appSettings.turnstileEnabled;

  const [guestName, setGuestName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState('');

  // Honeypot field (hidden from users, bots fill it)
  const [honeypot, setHoneypot] = useState('');

  const editorRef = useRef<HTMLDivElement>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);

  // Fade animation state
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  // Restore saved guest name
  useEffect(() => {
    if (isOpen) {
      try {
        const saved = localStorage.getItem(GUEST_NAME_KEY);
        if (saved) setGuestName(saved);
      } catch { /* ignore */ }
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Mount/unmount animation
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else if (mounted) {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, mounted]);

  // Render Turnstile widget — depends on `mounted` so the effect re-runs
  // once the DOM is actually rendered (turnstileRef.current becomes non-null).
  useEffect(() => {
    if (!isOpen || !mounted || !turnstileActive || !turnstileRef.current) return;

    const renderWidget = () => {
      if (turnstileWidgetId.current !== null) {
        (window as any).turnstile?.reset(turnstileWidgetId.current);
        return;
      }
      if ((window as any).turnstile && turnstileRef.current) {
        turnstileWidgetId.current = (window as any).turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_CONFIG.siteKey,
          callback: (token: string) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          // A failed challenge used to vanish silently; now it's an error report.
          'error-callback': (code: string) => {
            setTurnstileToken('');
            reportClientError({
              scope: 'turnstile',
              error: new Error(`Turnstile widget error ${code}`),
              extra: { turnstile_stage: 'widget', turnstile_code: String(code), turnstile_form: 'comment' },
            });
            return true; // handled — keep Cloudflare from logging it as unhandled
          },
          theme: 'light',
        });
      }
    };

    if ((window as any).turnstile) {
      renderWidget();
    } else {
      const existing = document.querySelector('script[src*="turnstile"]');
      if (!existing) {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.onload = () => setTimeout(renderWidget, 100);
        script.onerror = () => reportClientError({
          scope: 'turnstile',
          error: new Error('Turnstile script failed to load'),
          extra: { turnstile_stage: 'script', turnstile_form: 'comment' },
        });
        document.head.appendChild(script);
      } else {
        const checkReady = setInterval(() => {
          if ((window as any).turnstile) {
            clearInterval(checkReady);
            renderWidget();
          }
        }, 100);
        return () => clearInterval(checkReady);
      }
    }
  }, [isOpen, mounted, turnstileActive]);

  // Cleanup Turnstile widget on close
  useEffect(() => {
    if (!isOpen && turnstileWidgetId.current !== null) {
      try {
        (window as any).turnstile?.remove(turnstileWidgetId.current);
      } catch { /* ignore */ }
      turnstileWidgetId.current = null;
    }
  }, [isOpen]);

  // Rich text toolbar commands
  const execCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const commentHtml = editorRef.current?.innerHTML?.trim() || '';
    const commentText = editorRef.current?.textContent?.trim() || '';

    if (!commentText) {
      setError(t('comments.errors.empty'));
      return;
    }

    if (commentText.length > MAX_COMMENT_LENGTH) {
      // Formatted in the active locale — French groups with a space (5 000),
      // not a comma. Note this is NOT the house style: number formatting is
      // deliberately left in en-US across the app (see CommentThread's
      // formatDate). This one call site was written locale-aware when it was
      // added and there is no reason to walk it back.
      setError(t('comments.errors.tooLong', { max: MAX_COMMENT_LENGTH.toLocaleString(i18n.language) }));
      return;
    }

    if (turnstileActive && !turnstileToken) {
      setError(t('comments.errors.challenge'));
      return;
    }

    setSubmitting(true);
    try {
      // Save guest name for next time
      if (guestName.trim()) {
        try { localStorage.setItem(GUEST_NAME_KEY, guestName.trim()); } catch { /* ignore */ }
      }

      await postComment({
        guid: estimateGuid,
        parentId,
        // The stored fallback stays English: it is written to the database
        // and read by every viewer, whatever language each of them is using.
        guestName: guestName.trim() || 'Guest',
        commentText: commentHtml,
        turnstileToken,
        website: honeypot, // honeypot
      });

      // Clear editor
      if (editorRef.current) editorRef.current.innerHTML = '';
      setTurnstileToken('');

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('comments.errors.postFailed'));
      // Reset Turnstile on error
      if (turnstileWidgetId.current !== null) {
        try { (window as any).turnstile?.reset(turnstileWidgetId.current); } catch { /* ignore */ }
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!mounted) return null;

  return (
    <div
      className={`comment-modal-backdrop ${visible ? 'show' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`comment-modal ${visible ? 'show' : ''}`}>
        {/* Header */}
        <div className="comment-modal-header">
          <h3 className="comment-modal-title">
            {parentId
              ? t('comments.replyTo', { author: parentAuthor || t('comments.thisComment') })
              : t('comments.add')}
          </h3>
          <button
            type="button"
            className="comment-modal-close"
            onClick={onClose}
            aria-label={t('auth:close')}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="comment-modal-body">
            {/* Guest name field */}
            <div className="comment-field">
              <label htmlFor="comment-guest-name" className="comment-label">
                {t('comments.yourName')} <span className="comment-label-hint">{t('comments.optional')}</span>
              </label>
              <input
                id="comment-guest-name"
                type="text"
                className="comment-input"
                value={guestName}
                onChange={e => setGuestName(e.target.value)}
                placeholder={t('comments.guest')}
                maxLength={100}
                autoComplete="name"
              />
            </div>

            {/* Honeypot (hidden from real users) */}
            <div className="comment-hp" aria-hidden="true" tabIndex={-1}>
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={e => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {/* Rich text editor */}
            <div className="comment-field">
              <label className="comment-label">{t('comments.commentLabel')}</label>
              <div className="comment-editor-wrap">
                <div className="comment-toolbar">
                  <button
                    type="button"
                    className="comment-toolbar-btn"
                    onClick={() => execCommand('bold')}
                    title={t('comments.bold')}
                    aria-label={t('comments.bold')}
                  >
                    <Bold size={15} />
                  </button>
                  <button
                    type="button"
                    className="comment-toolbar-btn"
                    onClick={() => execCommand('italic')}
                    title={t('comments.italic')}
                    aria-label={t('comments.italic')}
                  >
                    <Italic size={15} />
                  </button>
                  <div className="comment-toolbar-sep" />
                  <button
                    type="button"
                    className="comment-toolbar-btn"
                    onClick={() => execCommand('insertUnorderedList')}
                    title={t('comments.bulletList')}
                    aria-label={t('comments.bulletList')}
                  >
                    <List size={15} />
                  </button>
                </div>
                <div
                  ref={editorRef}
                  className="comment-editor"
                  contentEditable
                  role="textbox"
                  aria-multiline="true"
                  aria-label={t('comments.editorAria')}
                  data-placeholder={t('comments.placeholder')}
                />
              </div>
            </div>

            {/* Turnstile widget */}
            {turnstileActive && (
              <div className="comment-turnstile">
                <div ref={turnstileRef} />
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="comment-error">{error}</div>
            )}
          </div>

          {/* Footer */}
          <div className="comment-modal-footer">
            <button
              type="button"
              className="comment-btn-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              {t('auth:cancel')}
            </button>
            <button
              type="submit"
              className="comment-btn-submit"
              disabled={submitting || (turnstileActive && !turnstileToken)}
            >
              {submitting ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  {t('comments.posting')}
                </>
              ) : (
                <>
                  <Send size={16} />
                  {t('comments.post')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
